<template>
  <div class="rulesets-form">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
      :loading="loading"
    >
      <template #actions v-if="!isOnlyView">
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="actionRuleset"
          >
            <span class="px-2">{{ rulesetId ? 'Save' : 'Create'}}</span>
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="d-flex align-items-center justify-content-between">
        <FormTitle title="General info" />
        <a-button
          v-if="isOnlyView"
          size="large"
          :disabled="!editPermission"
          @click="goToEdit"
        >
          <span class="px-2">Edit</span>
        </a-button>
      </div>
      <a-form-model :model="form" ref="form">
        <a-row class="d-flex">
          <Readonly
            :show="isOnlyView && !!form.name"
            title="Name"
            :value="form.name"
            style="max-width: 270px; align-self: flex-start;"
            class="mb-3"
          >
            <a-form-model-item
              label="Name"
              prop="name"
              :colon="false"
              :rules="[{ required: true, message: 'Name is required', trigger: ['blur', 'change'] }]"
              class="mr-5"
              style="max-width: 280px"
            >
              <a-input
                v-model="form.name"
                style="width: 280px;"
                placeholder="Name"
                size="large"
                allow-clear
                :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                :disabled="loading || isOnlyView"
              />
            </a-form-model-item>
          </Readonly>
        </a-row>
        <a-row class="d-flex">
          <Readonly
            :show="isOnlyView && !!currentRulesetRoleName || isEditMode"
            title="Role"
            :value="currentRulesetRoleName"
            style="max-width: 270px; align-self: flex-start;"
            :class="{'mb-4': isEditMode}"
          >
            <a-form-model-item
              v-if="availableRoles.length"
              prop="role_id"
              :colon="false"
              :rules="[{ required: true, message: 'Role is required', trigger: ['blur', 'change'] }]"
              class="label-with-tooltip"
            >
              <template #label>
                <div>
                  <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Role</span>
                  <a-tooltip
                    v-if="!isEditMode"
                    placement="top"
                    arrow-point-at-center
                    :overlayStyle="{ 'white-space': 'normal', 'min-width': '240px' }"
                    :get-popup-container="getPopupContainer"
                  >
                    <template slot="title">
                      <span>Can be changed only in create mode</span>
                    </template>
                    <a-icon class="d-flex" type="info-circle" />
                  </a-tooltip>
                </div>
              </template>
              <a-radio-group
                v-model="form.role_id"
                name="radioGroup"
                :disabled="isOnlyView"
                class="d-flex pb-2"
                style="gap: 20px;"
              >
                <a-radio
                  v-for="role in availableRoles"
                  :key="role.id"
                  :value="role.id"
                  :disabled="getAvailableRolesLoading"
                >
                  {{ role.name }}
                </a-radio>
              </a-radio-group>
            </a-form-model-item>
          </Readonly>
        </a-row>
        <FormTitle title="Rules" />
        <a-tabs
          v-model="activeLanguage"
          :animated="{ inkBar: true, tabPane: false }"
          @change="changeActiveLanguageTab"
        >
          <a-tab-pane
            v-for="langTab in form.languages"
            :key="langTab.language.id"
            force-render
          >
            <template #tab>
              <DefaultLabelTab
                :name="langTab.language.name"
                :error_validate="langTab.error_validate"
                :is_default="langTab.language.is_default"
              />
            </template>
            <div class="d-flex justify-content-between align-items-center">
              <a-button
                v-if="!isOnlyView"
                type="primary"
                size="large"
                :disabled="!createPermission"
                @click="toggleShowRuleModal(true)"
              >
                <a-icon type="plus" /> Add
              </a-button>
            </div>
            <div class="d-flex flex-wrap" style="max-width: 665px; gap: 15px;">
              <div v-if="!langTab.rules.length" class="text-secondary mt-4">
                <span v-if="langTab.error_validate" class="text-danger">The rules are mandatory</span>
                <span v-else>No rules have been added</span>
              </div>
              <div v-else class="mt-4">
                <draggable
                  v-model="langTab.rules"
                  v-bind="dragOptions"
                  handle=".handle"
                  @start="drag = true"
                  @end="drag = false"
                >
                  <transition-group>
                    <div
                      v-for="(rule, index) in langTab.rules"
                      :key="rule.uniqueId"
                      class="rule"
                    >
                      <div class="d-flex align-items-center mb-1">
                        <a-icon
                          v-if="!isOnlyView"
                          type="menu"
                          class="drag-icon handle rulesets-form__example__icon rulesets-form__example__icon__hide"
                          :class="{'rulesets-form__example__icon__show mr-2' : langTab.rules.length > 1}"
                        />
                        <span class="font-weight-bold">Rule {{ index + 1 }}</span>
                      </div>
                      <div
                        class="d-flex"
                        :class="{'rule__pl-28' : !isOnlyView}"
                        style="gap: 20px"
                      >
                        <ImageWithBlurHash
                          :imageSrc="rule.imageUrl"
                          :blurhash="rule.blurHash"
                          :width="150"
                          :height="150"
                          :alt="`${index + 1} Rule image`"
                          :timeoutBlurHash="100"
                        />
                        <div class="d-flex flex-column rule__content" style="max-width: 350px; min-width: 350px; gap: 10px">
                          <div class="d-flex flex-column">
                            <span class="font-weight-bold">Title</span>
                            <span class="rule__title">{{ rule.name }}</span>
                          </div>
                          <div class="d-flex flex-column">
                            <span class="font-weight-bold">Description</span>
                            <span class="rule__description">{{ rule.description }}</span>
                          </div>
                          <div class="d-flex justify-content-end">
                            <a-button
                              v-if="!isOnlyView"
                              type="link"
                              :disabled="!editPermission"
                              @click="toggleShowRuleModal(true, rule)"
                            >
                              <a-icon type="edit" /> Edit
                            </a-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition-group>
                </draggable>
              </div>
            </div>
          </a-tab-pane>
          <span
            v-if="!isOnlyView"
            slot="tabBarExtraContent"
            class="text-primary cursor-pointer underline"
            @click="toggleShowEditLanguagesModal(true)"
          >
            Edit languages
          </span>
        </a-tabs>
      </a-form-model>
    </MainContainer>
    <div v-if="!isOnlyView && isEditMode && deletePermission" class="mt-3">
      <div
        class="d-flex align-items-center text-danger cursor-pointer hover-underline"
        style="width: fit-content;"
        @click="toggleShowDeleteModal(true)"
      >
        <a-icon type="delete" class="mr-2" />
        <span>Delete Ruleset</span>
      </div>
    </div>
    <EditLanguagesModal
      v-if="showEditLanguagesModal"
      :show="showEditLanguagesModal"
      :allLanguages="availableLanguages"
      :languagesUsed="languagesUsed"
      @update="updateLanguages"
      @close="toggleShowEditLanguagesModal"
    />
    <AddRuleModal
      v-if="showRuleModal"
      :show="showRuleModal"
      :selectedRule="editableRule"
      @create="createRule"
      @update="updateRule"
      @delete="deleteRule"
      @close="toggleShowRuleModal"
    />
    <DefaultModal
      :show="showDeleteModal"
      title="Delete Ruleset"
      text="Are you sure you want to delete this Ruleset?"
      description="All associated data will be lost"
      :actionButton="{
        text: 'Delete',
        type: 'danger',
      }"
      :loading="loading"
      @close="toggleShowDeleteModal"
      @action="removeRuleset"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import ImageWithBlurHash from '@/components/custom/image/ImageWithBlurHash.vue'
import AddRuleModal from '@/views/apps/referral-program/components/AddRuleModal.vue'
import DefaultModal from '@/components/custom/modals/defaultModal'
import EditLanguagesModal from '@/views/apps/referral-program/components/EditLanguagesModal.vue'
import draggable from 'vuedraggable'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'
import { getReferralLanguages } from '@/views/apps/referral-program/composables/useGetReferralLanguages.js'
import { getRandomId } from '@/composables/useGetRandomId.js'
import { defaultLanguageName } from '@/views/apps/referral-program/constants/defaultLanguageName.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { addArrayToFormData } from '@/services/utils/formData.util.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    FormTitle,
    DefaultLabelTab,
    ImageWithBlurHash,
    draggable,
    Readonly,
    EditLanguagesModal,
    AddRuleModal,
    DefaultModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    rulesetId: null,
    currentRuleset: null,
    editableRule: null,
    availableRoles: [],
    getAvailableRolesLoading: false,
    availableLanguages: [],
    getAvailableLanguagesLoading: false,
    activeLanguage: 1,
    disableCopy: false,
    form: {
      name: '',
      role_id: undefined,
      languages: {},
    },
    loading: false,
    showEditLanguagesModal: false,
    showRuleModal: false,
    showDeleteModal: false,
    drag: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isOnlyView() {
      return this.$route.name === 'view-rulesets'
    },
    isEditMode() {
      return !!this.rulesetId
    },
    breadcrumbs() {
      if (this.currentRuleset) {
        if (this.isOnlyView) {
          return [
            { name: 'Rulesets', link: '/referral/rulesets' },
            { name: 'View ruleset', link: undefined },
          ]
        }
        return [
          { name: 'Rulesets', link: '/referral/rulesets' },
          { name: this.currentRuleset?.name, link: `/referral/rulesets/${this.currentRuleset.id}/view` },
          { name: 'Edit ruleset', link: undefined },
        ]
      }
      return [
        { name: 'Rulesets', link: '/referral/rulesets' },
        { name: 'Create ruleset', link: undefined },
      ]
    },
    dragOptions() {
      return {
        animation: 300,
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    activeLanguageName() {
      const activeLanguage = this.availableLanguages.find(lang => lang.id === this.activeLanguage)
      if (activeLanguage) {
        return activeLanguage.name
      }
      return ''
    },
    languagesUsed() {
      return Object.values(this.form.languages).map(lang => ({
        name: lang.language.name,
        value: lang.language.id,
        is_default: lang.language.is_default,
      }))
    },
    currentRulesIds() {
      if (this.form.languages[this.activeLanguageName].rules?.length) {
        return this.form.languages[this.activeLanguageName].rules.map(rule => rule.id)
      }
      return []
    },
    currentRulesetRoleName() {
      return this.currentRuleset?.role?.name
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    goToEdit() {
      this.$router.push(`/referral/rulesets/${this.rulesetId}`)
    },
    setAvailableRulesLanguages(languages = []) {
      languages.forEach(lang => {
        this.setAvailableLanguage(lang)
      })
    },
    setAvailableLanguage(language, rules = []) {
      this.$set(
        this.form.languages,
        language.name,
        {
          language: { id: language.id, name: language.name, is_default: language.is_default },
          rules: rules,
          error_validate: false,
        },
      )
    },
    changeActiveLanguageTab() {
      this.clearError()
    },
    clearError() {
      this.form.languages[this.activeLanguageName].error_validate = false
    },
    toggleShowEditLanguagesModal(status = false) {
      this.showEditLanguagesModal = status
    },
    toggleShowRuleModal(status = false, rule = null) {
      this.editableRule = rule
      this.showRuleModal = status
    },
    toggleShowDeleteModal(status = false) {
      this.showDeleteModal = status
    },
    updateLanguages(newLanguages) {
      const sortedLanguages = newLanguages.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
      const newLangNames = sortedLanguages.map(lang => lang.name)

      // Обновляем языки
      sortedLanguages.forEach(lang => {
        if (!this.form.languages[lang.name]) {
          this.setAvailableLanguage({ ...lang, id: lang.value })
        } else {
          this.setAvailableLanguage({ ...lang, id: lang.value }, this.form.languages[lang.name].rules)
        }
      })

      // Удаляем языки, которых нет в newLanguages
      Object.keys(this.form.languages).forEach(existingLangName => {
        if (!newLangNames.includes(existingLangName)) {
          const isActiveLangBeingDeleted = (existingLangName === this.activeLanguageName)

          this.$delete(this.form.languages, existingLangName)

          // Если удалили активный язык — обновляем activeLanguage
          if (isActiveLangBeingDeleted) {
            const remainingLanguages = Object.values(this.form.languages)
            if (remainingLanguages.length > 0) {
              this.activeLanguage = remainingLanguages[0].language.id
            } else {
              this.activeLanguage = null
            }
          }
        }
      })

      // Переупорядочиваем form.languages по отсортированным newLangNames
      const reorderedLanguages = {}
      newLangNames.forEach(langName => {
        if (this.form.languages[langName]) {
          reorderedLanguages[langName] = this.form.languages[langName]
        }
      })

      // Очищаем старый объект, чтобы сохранить реактивность
      Object.keys(this.form.languages).forEach(key => {
        this.$delete(this.form.languages, key)
      })

      // Записываем заново, в нужном порядке
      Object.entries(reorderedLanguages).forEach(([key, value]) => {
        this.$set(this.form.languages, key, value)
      })
    },
    createRule(rule) {
      this.form.languages[this.activeLanguageName].rules.push({ uniqueId: getRandomId(this.currentRulesIds), ...rule })
      this.form.languages[this.activeLanguageName].error_validate = false
    },
    updateRule(updatedRule) {
      const oldRuleIdx = this.form.languages[this.activeLanguageName].rules.findIndex(el => el.uniqueId === updatedRule.uniqueId)
      if (oldRuleIdx === -1) {
        return
      }
      this.form.languages[this.activeLanguageName].rules.splice(oldRuleIdx, 1, updatedRule)
    },
    deleteRule(deleteRule) {
      const oldRuleIdx = this.form.languages[this.activeLanguageName].rules.findIndex(el => el.uniqueId === deleteRule.uniqueId)
      if (oldRuleIdx === -1) {
        return
      }
      this.form.languages[this.activeLanguageName].rules.splice(oldRuleIdx, 1)
    },
    checkingForTabFilling() {
      let isValidRules = true
      const languageKeys = Object.keys(this.form.languages)

      languageKeys.forEach((langKey) => {
        if (!this.form.languages[langKey].rules?.length) {
          this.form.languages[langKey].error_validate = true
          isValidRules = false
        } else {
          this.form.languages[langKey].error_validate = false
        }
      })
      return isValidRules
    },
    actionRuleset() {
      // Нужно чтобы отобразить в каких табах есть ошибки валидации
      const isValidRules = this.checkingForTabFilling()

      this.$refs.form.validate(async (valid) => {
        if (!valid || !isValidRules) {
          this.$notification.error({
            message: 'Please fill in all required fields',
          })
          return false
        }

        const data = this.getPayloadData()
        if (this.isEditMode) {
          this.updateRuleset(data)
          return
        }
        this.createRuleset(data)
      })
    },
    getPayloadData() {
      const formData = new FormData()

      if (this.isEditMode) {
        formData.append('_method', 'PUT')
      }

      formData.append('name', this.form.name)
      formData.append('role_id', this.form.role_id)

      const items = Object.values(this.form.languages).flatMap(({ language, rules }) =>
        rules.map(({ id, name, description, image }) => {
          const media = image instanceof File ? image : image?.id
          if (id) {
            return { language_id: language.id, id, name, description, media }
          }
          return { language_id: language.id, name, description, media }
        }))

      formData.append('default_language_id', items[0].language_id)
      addArrayToFormData(formData, items, 'items')

      return formData
    },
    async createRuleset(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/rule-sets`
        const response = await apiClient.post(url, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Rulesets successfully created',
            description: '',
          })
          this.$router.push('/referral/rulesets')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async updateRuleset(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/rule-sets/${this.rulesetId}`
        const response = await apiClient.put(url, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Rulesets successfully updated',
            description: '',
          })
          this.$router.push(`/referral/rulesets/${this.rulesetId}/view`)
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async removeRuleset() {
      try {
        this.loading = true
        const url = `${referralApiUrl}/rule-sets/${this.rulesetId}`
        const response = await apiClient.delete(url)
        if (response?.status >= 200 && response?.status < 300) {
          this.$notification.success({
            message: 'Rulesets successfully removed',
            description: '',
          })
          this.$router.push('/referral/rulesets')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async getRuleSetParams() {
      try {
        const url = `${referralApiUrl}/rule-sets/${this.rulesetId}`

        const response = await apiClient.get(url)
        if (response?.data?.data) {
          const { data } = response.data
          this.currentRuleset = data
          this.form.name = data.name
          this.form.role_id = data.role.id

          data.items.forEach(item => {
            const filledItem = {
              id: item.id,
              name: item.name,
              description: item.description,
              imageUrl: item.media.src,
              blurHash: item.media.blurhash,
              image: item.media,
              uniqueId: item.id,
            }
            const currentItems = this.form.languages[item.language.name]?.rules || []
            this.setAvailableLanguage(
              { ...item.language },
              [...currentItems, filledItem],
            )
          })
          this.activeLanguage = data.items[0].language?.id || 1
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getAvailableRoles() {
      try {
        this.getAvailableRolesLoading = true
        this.availableRoles = await getReferralRoles() || []
      } finally {
        this.getAvailableRolesLoading = false
      }
    },
    async getAvailableLanguages() {
      try {
        this.getAvailableLanguagesLoading = true
        const allLanguages = await getReferralLanguages() || []
        if (allLanguages) {
          this.availableLanguages = allLanguages.map(lang => {
            const langObject = {
              id: lang.id,
              name: lang.name,
            }
            if (lang.name === defaultLanguageName && !this.rulesetId) {
              this.updateLanguages([{
                name: lang.name,
                value: lang.id,
                is_default: true,
              }])
            }
            return langObject
          })
        }
      } finally {
        this.getAvailableLanguagesLoading = false
      }
    },
  },
  async mounted() {
    this.rulesetId = this.$route.params.id

    try {
      this.loading = true
      const requests = [this.getAvailableRoles(), this.getAvailableLanguages()]

      if (this.rulesetId) {
        requests.push(this.getRuleSetParams())
      }
      await Promise.all(requests)
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss">
.rulesets-form {
  &__example {
    transition: width 2s ease;
    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: flex-start;
      height: 40px;
      width: 20px;
      min-width: 20px;
      transition: all .3s ease;
      &__hide {
        width: 0;
        min-width: 0;
      }
      &__show {
        width: 20px;
        min-width: 20px;
      }
    }
  }
  .rule {
    &__content {
      max-width: 500px;
    }
    &__title {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__description {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    &__pl-28 {
      padding-left: 28px;
    }
  }
}
</style>
